import React, { useEffect, useMemo } from 'react';
import type { TabbedPageComponentProps } from '../TabbedPage.config';
import {
  useAdManagementUserContractsApiHandler_getDocumentEditorUrl,
  useAdManagementUserContractsApiHandler_getSubscriptionContractDetails,
} from '@zg-rentals/ts-ad-management-client-react-query';
import { Alert, Button, Paragraph, Spinner, Table, Toast, useToast } from '@zillow/constellation-10';
import { PreviewContractModal } from './PreviewContractModal';
import { formatLocalDate } from '@/shared/utils/dateUtils';
import { logError } from '@zg-rentals/log-error';
import type {
  AdManagementSubscriptionContractsDto,
  AdManagementZdocsDocumentDto,
} from '@zg-rentals/ts-ad-management-client';
import { AdornedInput, Box, FormField, Label, Tag, TextButton } from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';
import { getSubscriptionStatusConfig, getZuoraBillingStatusConfig } from './ContractsPage.config';
import { TabbedPageEnum } from '../TabbedPage.config';
import { useAuth } from '@/AuthProvider';
import { useRouter } from 'next/router';
import { emitClickContactSalesEvent, emitViewContractEvent } from '@/analytics/events';

const CONTRACTS_LIMIT = 10;

export const ContractsPage: React.FC<TabbedPageComponentProps> = () => {
  const [documentId, setDocumentId] = React.useState<string>('');
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [showMoreContracts, setShowMoreContracts] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const router = useRouter();
  const { enqueueToast } = useToast();
  const { user } = useAuth();

  const {
    data: contracts,
    isPending: loadingContracts,
    error: contractsError,
  } = useAdManagementUserContractsApiHandler_getSubscriptionContractDetails();

  const {
    isFetching: isFetchingPreviewUrl,
    data: contractUrl,
    error: previewError,
  } = useAdManagementUserContractsApiHandler_getDocumentEditorUrl({ documentId }, { enabled: Boolean(documentId) });

  const onClickPreview = async (documentId: string) => {
    if (isFetchingPreviewUrl) return;
    setDocumentId(documentId);
    setIsModalOpen(true);
    if (user) emitViewContractEvent({ user });
  };

  useEffect(() => {
    if (!previewError) return;

    enqueueToast(<Toast tone="critical">Failed to submit request, please try again later.</Toast>);
    logError({
      error: previewError,
      errorType: 'FetchContractPreviewError',
    });
  }, [previewError, enqueueToast]);

  const mappedContracts = useMemo(
    () =>
      contracts?.flatMap((contract) =>
        contract.documents
          ? contract.documents.map((doc) => ({ ...doc, ...contract }))
          : [{ ...contract } as AdManagementSubscriptionContractsDto & AdManagementZdocsDocumentDto],
      ) ?? [],
    [contracts],
  );

  const filteredContracts = useMemo(
    () => mappedContracts.filter((contract) => contract.propertyName.toLowerCase().includes(searchQuery.toLowerCase())),
    [mappedContracts, searchQuery],
  );

  const contractsToShow = useMemo(() => {
    return !showMoreContracts ? filteredContracts.slice(0, CONTRACTS_LIMIT) : filteredContracts;
  }, [filteredContracts, showMoreContracts]);

  const onClickContactSales = () => {
    router.push(`/self-serve-data/${TabbedPageEnum.SalesPro}`);
    if (user) emitClickContactSalesEvent({ user });
  };

  if (loadingContracts)
    return (
      <Box css={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner size="lg" />
      </Box>
    );

  if (!contracts || contractsError)
    return (
      <Alert tone="critical" css={{ margin: 'md' }}>
        Failed to load contracts...
      </Alert>
    );

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        css={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          marginBottom: 'md',
        }}
      >
        <FormField
          css={{
            maxWidth: '300px',
          }}
          label={<Label>Search</Label>}
          control={
            <AdornedInput
              endAdornment={
                <AdornedInput.Adornment>
                  <AdornedInput.Icon>
                    <IconSearchFilled />
                  </AdornedInput.Icon>
                </AdornedInput.Adornment>
              }
              input={
                <AdornedInput.Input
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowMoreContracts(false);
                  }}
                />
              }
              size="md"
              startAdornment={null}
            />
          }
        />
      </Box>
      {contractsToShow.length > 0 ? (
        <Table.Responsive>
          <Table.Root aria-label="contracts table" appearance="zebra" size="md">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Property name</Table.HeaderCell>
                <Table.HeaderCell>Effective date</Table.HeaderCell>
                <Table.HeaderCell>End date</Table.HeaderCell>
                <Table.HeaderCell>Document name</Table.HeaderCell>
                <Table.HeaderCell>Product Type</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>&nbsp;</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {contractsToShow.map((row) => {
                const subscriptionStatusConfig = getSubscriptionStatusConfig(row.subscriptionProductStatus);
                const zuoraBillingStatusConfig = getZuoraBillingStatusConfig(row.zuoraBillingStatus);
                const startDate = row.productType === 'SFR' ? row.orderEffectiveDate : row.subscriptionProductStartDate;

                return (
                  <Table.Row key={row.documentId}>
                    <Table.Cell>{row.propertyName}</Table.Cell>
                    <Table.Cell>{startDate ? formatLocalDate(startDate) : '--'}</Table.Cell>
                    <Table.Cell>
                      {row.subscriptionProductEndDate ? formatLocalDate(row.subscriptionProductEndDate) : '--'}
                    </Table.Cell>
                    <Table.Cell>{row.documentName ?? '--'}</Table.Cell>
                    <Table.Cell>{row.productType === 'SFR' ? 'Single Family' : row.productType}</Table.Cell>
                    <Table.Cell>
                      {row.productType !== 'SFR' ? (
                        <Tag tone={subscriptionStatusConfig.tone}>{subscriptionStatusConfig.label}</Tag>
                      ) : (
                        <Tag tone={zuoraBillingStatusConfig.tone}>{zuoraBillingStatusConfig.label}</Tag>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {row.documentId && (
                        <Button
                          onClick={() => onClickPreview(row.documentId)}
                          loading={row.documentId === documentId && isFetchingPreviewUrl}
                        >
                          Preview
                        </Button>
                      )}
                      {!row.documentId && (
                        <Tag onClick={onClickContactSales} tone="info">
                          Contact Sales
                        </Tag>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
          {filteredContracts.length > CONTRACTS_LIMIT && (
            <Box
              css={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                marginTop: 'md',
              }}
            >
              <TextButton onClick={() => setShowMoreContracts(!showMoreContracts)}>
                {showMoreContracts ? 'Show less' : 'Show more'}
              </TextButton>
            </Box>
          )}
        </Table.Responsive>
      ) : (
        <Paragraph>Nothing to show here...</Paragraph>
      )}
      <PreviewContractModal
        contractUrl={contractUrl ?? ''}
        isOpen={Boolean(isModalOpen && contractUrl)}
        onClose={() => setIsModalOpen(false)}
      />
    </Box>
  );
};
